import {
  Box,
  BoxProps,
  Divider,
  Grid,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FiDollarSign, FiTag, FiUser } from "react-icons/fi";
import { IoPricetagsOutline } from "react-icons/io5";
import { CreateSegmentFormValues as FormValues } from ".";
import { KYDNumericOperator } from "common/interfaces/KYDCampaign";
import { genderToText } from "common/services/util/campaigns";
import { NumericValueDisplay } from "./NumericValueDisplay";
import { FilterSummary } from "./FilterSummary";
import { dineroFormatCurrency } from "src/utils";
import {
  InstantSearch,
  useConfigure,
  useInfiniteHits,
} from "react-instantsearch-core";
import { SearchClient } from "algoliasearch";

const Card = ({
  title,
  children,
  icon,
  ...props
}: {
  title: string;
  children: React.ReactNode;
  icon: React.ReactNode;
} & BoxProps) => {
  return (
    <Box
      display="flex"
      w="100%"
      h="100%"
      flex="1"
      padding="16px 0px"
      flexDirection="column"
      justifyContent="space-between"
      alignItems="flex-start"
      flexShrink={0}
      borderRadius="10px"
      border="1px solid #E6E6E6"
      background="#FFF"
      boxShadow="0px 0px 10px 0px rgba(0, 0, 0, 0.05)"
      py={4}
      px={3}
      {...props}
    >
      <HStack
        alignItems="center"
        justifyContent="space-between"
        w="100%"
        mb={6}
      >
        <Text
          color="rgba(34, 34, 34, 0.80)"
          fontSize="16px"
          fontWeight={500}
          lineHeight="23px"
          letterSpacing="-0.48px"
        >
          {title}
        </Text>
        {icon}
      </HStack>
      {children}
    </Box>
  );
};

export const VenueFilterSummary = ({ venues }: { venues: string[] }) => {
  const { items } = useInfiniteHits();
  useConfigure({
    restrictSearchableAttributes: ["venues.name"],
    hitsPerPage: 10,
  });

  const uniqueVenues = new Set(
    items
      .map((event) => event.venues[0])
      .filter((venue) => venues.includes(venue.id))
      .map((venue) => venue.name)
  );

  return (
    <FilterSummary
      title="Venue"
      count={uniqueVenues.size.toString()}
      values={Array.from(uniqueVenues)}
    />
  );
};

export const PerformerFilterSummary = ({
  performers,
}: {
  performers: string[];
}) => {
  const { items } = useInfiniteHits();
  useConfigure({
    restrictSearchableAttributes: ["performers.name"],
    hitsPerPage: 10,
  });

  const uniquePerformers = new Set(
    items
      .reduce((performers, event) => [...performers, ...event.performers], [])
      .filter((performer) => performers.includes(performer.id))
      .map((performer) => performer.name)
  );

  return (
    <FilterSummary
      title="Performer"
      count={uniquePerformers.size.toString()}
      values={Array.from(uniquePerformers)}
    />
  );
};

export const EventFilterSummary = ({ events }: { events: string[] }) => {
  const { items } = useInfiniteHits();
  useConfigure({
    restrictSearchableAttributes: ["name"],
    hitsPerPage: 10,
  });

  const uniqueEvents = new Set(
    items
      .filter((event) => events.includes(event.id))
      .map((event) => event.name)
  );

  return (
    <FilterSummary
      title="Event"
      count={uniqueEvents.size.toString()}
      values={Array.from(uniqueEvents)}
    />
  );
};

const ReviewCreateSegment = ({
  data,
  segmentSize,
  eventsAlgoliaConfig,
}: {
  data: FormValues;
  segmentSize: number;
  eventsAlgoliaConfig: {
    indexName: string;
    searchClient: SearchClient;
  };
}) => {
  const hasPurchaseBehaviourFilters =
    data.filters?.average_order_value.value > 0 ||
    data.filters.average_items_per_order.value > 0 ||
    data.filters.order_count.value > 0 ||
    data.filters.total_attendance.value > 0;

  const hasDemographicsFilters =
    data.filters?.age.value > 0 ||
    (data.filters?.gender !== null && data.filters?.gender !== "any");

  const hasEventBasedFilters =
    data.filters?.genres.length > 0 ||
    data.filters?.venues.length > 0 ||
    data.filters?.artists.length > 0 ||
    data.filters?.events.length > 0;

  return (
    <VStack align="flex-start">
      <Divider borderBottom="1px solid rgba(0, 0, 0, 0.20)" mb={6} />
      <Text
        color="rgba(34, 34, 34, 0.80)"
        fontSize="16px"
        fontWeight={500}
        lineHeight="23px"
        letterSpacing="-0.48px"
      >
        Segment Name:
      </Text>
      <Text
        color="#1A202C"
        fontSize="20px"
        fontWeight={400}
        lineHeight="120%"
        letterSpacing="-0.6px"
      >
        {data.name}
      </Text>
      <HStack mt={4}>
        <Box borderRight="1px solid rgba(0, 0, 0, 0.20)" pr={4}>
          <VStack align="flex-start">
            <Text
              color="rgba(34, 34, 34, 0.80)"
              fontSize="16px"
              fontWeight={500}
              lineHeight="23px"
              letterSpacing="-0.48px"
            >
              Segment size:
            </Text>
            <Text
              color="#1A202C"
              fontSize="20px"
              fontWeight={400}
              lineHeight="120%"
              letterSpacing="-0.6px"
            >
              {Number(segmentSize).toLocaleString()} Contacts
            </Text>
          </VStack>
        </Box>
        <VStack align="flex-start">
          <Text
            color="rgba(34, 34, 34, 0.80)"
            fontSize="16px"
            fontWeight={500}
            lineHeight="23px"
            letterSpacing="-0.48px"
          >
            Auto Update
          </Text>
          <Text
            color="#1A202C"
            fontSize="20px"
            fontWeight={400}
            lineHeight="120%"
            letterSpacing="-0.6px"
          >
            {data.type === "dynamic" ? "On" : "Off"}
          </Text>
        </VStack>
      </HStack>

      <Grid
        templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
        width={"100%"}
        gap="16px"
        alignItems="stretch"
        flex="1"
        mt={6}
      >
        {hasEventBasedFilters ? (
          <Card title="Event Based" icon={<Icon as={FiTag} />}>
            <InstantSearch
              searchClient={eventsAlgoliaConfig?.searchClient}
              indexName={eventsAlgoliaConfig?.indexName}
            >
              <VStack gap={6} w="100%" align="stretch">
                {data.filters.genres.length > 0 && (
                  <FilterSummary
                    title="Genre"
                    count={data.filters.genres.length.toString()}
                    values={data.filters.genres.map((genre) => genre.label)}
                  />
                )}
                {data.filters.events.length > 0 && (
                  <EventFilterSummary
                    events={data.filters.events.map((event) => event.value)}
                  />
                )}
                {data.filters.artists.length > 0 && (
                  <PerformerFilterSummary
                    performers={data.filters.artists.map(
                      (performer) => performer.value
                    )}
                  />
                )}

                {data.filters.venues.length > 0 && (
                  <VenueFilterSummary
                    venues={data.filters.venues.map((venue) => venue.value)}
                  />
                )}
              </VStack>
            </InstantSearch>
          </Card>
        ) : null}
        {data.filters.age.value ? (
          <Card
            title="Demographics"
            icon={<Icon as={FiUser} />}
            flex={1}
            h="100%"
          >
            <HStack
              gap={6}
              w="100%"
              justifyContent="space-between"
              alignItems="flex-start"
              h="100%"
            >
              <NumericValueDisplay
                title="Age"
                value={data.filters.age.value.toString()}
                operator={data.filters.age.operator}
              />
              <NumericValueDisplay
                title="Gender"
                value={genderToText(data.filters.gender)}
              />
            </HStack>
          </Card>
        ) : null}
        {hasPurchaseBehaviourFilters ? (
          <Card
            title="Purchase Behavior"
            flex={1}
            icon={<Icon as={FiDollarSign} />}
            h="100%"
            alignItems="flex-start"
            justifyContent="flex-start"
          >
            <Grid
              templateColumns="repeat(2, 1fr)"
              gap={6}
              w="100%"
              justifyContent="space-between"
            >
              <NumericValueDisplay
                title="Avg. Basket Size"
                value={dineroFormatCurrency(
                  data.filters.average_order_value.value * 100
                )}
                operator={data.filters.average_order_value.operator}
              />
              <NumericValueDisplay
                title="Avg. Items Per Order"
                value={data.filters.average_items_per_order.value.toString()}
                operator={data.filters.average_items_per_order.operator}
              />
              <NumericValueDisplay
                title="Order Count"
                value={data.filters.order_count.value.toString()}
                operator={data.filters.order_count.operator}
              />
              <NumericValueDisplay
                title="Total Attendance"
                value={data.filters.total_attendance.value.toString()}
                operator={data.filters.total_attendance.operator}
              />
            </Grid>
          </Card>
        ) : null}
        {data.filters.custom_tags.length > 0 ? (
          <Card
            title="Tags"
            flex={1}
            icon={<Icon as={IoPricetagsOutline} />}
            h="100%"
          >
            {data.filters.custom_tags.length > 0 ? (
              <HStack
                gap={2}
                w="100%"
                alignItems="flex-start"
                h="100%"
                flexWrap="wrap"
                flexDir={"column"}
              >
                {data.filters.custom_tags.map((tag) => (
                  <Box
                    key={tag.value}
                    bg="#EDF2F7"
                    borderRadius="4px"
                    px={1.5}
                    color="rgba(0, 0, 0, 0.70)"
                    fontWeight={500}
                    fontSize="14px"
                    lineHeight="23px"
                  >
                    {tag.label}
                  </Box>
                ))}
              </HStack>
            ) : null}
          </Card>
        ) : null}
      </Grid>
    </VStack>
  );
};

export default ReviewCreateSegment;
